/**
 * Structure-management proposal card (the runtime HITL gate) shared by
 * rename, delete, duplicate, replace contours, structure type, markers and
 * the structure-set label / description / approval / new-set tools. These
 * changes have no geometry to preview, so the card states the change in
 * words: a summary, any facts the clinician should weigh, a highlighted
 * caution for the riskiest changes (delete, approval), and whether the
 * viewer can undo it. Pure presentational; nothing changes until the
 * clinician presses Apply. Frame, status chip and Apply/Reject live in
 * {@link ProposalCardShell}.
 */
import { Typography } from '@mui/material';
import { AlertTriangle } from 'lucide-react';
import ProposalCardShell, {
    ProposalSummaryBox,
    type ProposalStatus,
} from './ProposalCardShell';

export interface StructureChangeProposalCardProps {
    readonly title: string;
    readonly summary: string;
    readonly details: ReadonlyArray<string>;
    readonly undoable: boolean;
    readonly caution?: string;
    readonly status: ProposalStatus;
    readonly failureReason?: string;
    readonly onApply: () => void;
    readonly onReject: () => void;
    readonly onPreviewEnter: () => void;
    readonly onPreviewLeave: () => void;
}

export default function StructureChangeProposalCard({
    title,
    summary,
    details,
    undoable,
    caution,
    status,
    failureReason,
    onApply,
    onReject,
    onPreviewEnter,
    onPreviewLeave,
}: StructureChangeProposalCardProps) {
    return (
        <ProposalCardShell
            title={title}
            ariaLabel={`${title} proposal`}
            status={status}
            failureReason={failureReason}
            onApply={onApply}
            onReject={onReject}
            onPreviewEnter={onPreviewEnter}
            onPreviewLeave={onPreviewLeave}
        >
            <ProposalSummaryBox>{summary}</ProposalSummaryBox>

            {details.map((line) => (
                <Typography key={line} variant="caption" color="text.secondary">
                    {line}
                </Typography>
            ))}

            {caution && (
                <Typography
                    variant="caption"
                    color="warning.main"
                    sx={{ display: 'flex', alignItems: 'center' }}
                >
                    <AlertTriangle
                        size={12}
                        style={{ marginRight: 4, flexShrink: 0 }}
                    />
                    {caution}
                </Typography>
            )}

            <Typography variant="caption" color="text.secondary">
                {undoable
                    ? 'Same as the viewer action. Undo restores it.'
                    : 'Same as the viewer action. This cannot be undone in ' +
                      'the viewer.'}
            </Typography>
        </ProposalCardShell>
    );
}
